import type { Question, Submission } from '@l2u/ui/lib/types.ts';
import { expiryState } from '@l2u/ui/lib/expiry.ts';
import { questionCompletion } from '@l2u/ui/lib/visibility.ts';
import { isUnengagedDocumentRow } from '@l2u/shared/progress';

export type UploadDocStats = { current: number; expiring: number; expired: number };

// Tally a vendor's live uploaded files by expiry health, using the shared
// expiryState() helper so the Home stat card and the Documents summary chips
// always agree with the per-file chips. Counted per FILE (the admin-side
// complianceDocStats counts per question row, a different granularity).
// Adoptions are not counted — they carry no expiry. The /api/me read paths
// already exclude superseded files; the filter here is defensive.
export function uploadDocStats(submissions: Submission[], now: Date = new Date()): UploadDocStats {
  const stats: UploadDocStats = { current: 0, expiring: 0, expired: 0 };
  for (const s of submissions) {
    for (const f of s.submission_files) {
      if (f.superseded_at) continue;
      switch (expiryState(f, now)) {
        case 'expired':
          stats.expired += 1;
          break;
        case 'expiring':
          stats.expiring += 1;
          break;
        default: // 'ok' | 'none' — provided and healthy
          stats.current += 1;
      }
    }
  }
  return stats;
}

// Question types that ARE a document requirement in their own right — an
// unanswered one means the vendor owes a document. A plain question that merely
// carries `requires_upload` only owes its file once the vendor has engaged with
// it (the completion engine then says 'upload_required').
const DOC_QUESTION_TYPES = new Set(['document_upload', 'document_adopt', 'multiselect_uploads']);

// Completion reasons that mean "a document is missing", as opposed to "the
// question hasn't been answered".
const DOC_OWED_REASONS = new Set(['upload_required', 'adoption_required', 'expiry_required']);

/**
 * Task 4: how many documents the vendor still needs to act on — visible,
 * non-retired questions that can carry a document and that nothing currently
 * satisfies (no live file, no adoption, or a missing required expiry).
 *
 * The per-question verdict is delegated to the shared completion engine via
 * questionCompletion, so this count can never disagree with the progress bar or
 * the submit gate about whether a document is outstanding.
 *
 * Optional questions are excluded when simply unanswered (nothing is owed yet),
 * but still counted once the vendor has engaged and left a document missing.
 */
export function docActionCount(
  questions: Question[],
  submissions: Submission[],
  visibleIds: Set<string>,
): number {
  const subByQ = new Map(submissions.map((s) => [s.question_id, s]));
  let count = 0;
  for (const q of questions) {
    if (!visibleIds.has(q.id) || q.deleted_at) continue;
    if (!DOC_QUESTION_TYPES.has(q.type) && !q.requires_upload) continue;
    const reason = questionCompletion(q, subByQ.get(q.id));
    if (reason === null) continue;
    // An optional document question whose row was emptied (file uploaded then
    // removed) owes nothing — same rule the submit gate applies, shared so the
    // badge cannot disagree with it.
    if (!q.required && isUnengagedDocumentRow(q, reason)) continue;
    if (DOC_OWED_REASONS.has(reason)) count += 1;
    else if (reason === 'no_answer' && q.required && DOC_QUESTION_TYPES.has(q.type)) count += 1;
  }
  return count;
}
